import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { LOGO, COMPANY } from "@/lib/assets";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  FileText, Inbox, LayoutDashboard, LogOut, Plus, MoreVertical, Pencil, Trash2, Mail, ExternalLink,
} from "lucide-react";

const CATEGORIES = ["Industry", "Operations", "Sustainability", "News"];
const emptyPost = { title: "", excerpt: "", content: "", category: "News", cover_image: "", author: "Paptech Corp", published: true };
const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="border border-border bg-card p-6" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-start justify-between">
        <span className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{label}</span>
        <Icon size={18} className="text-primary" />
      </div>
      <div className="font-display text-5xl mt-4">{value}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [posts, setPosts] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPost);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, p, inq] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/blog"),
        api.get("/admin/inquiries"),
      ]);
      setStats(s.data);
      setPosts(p.data);
      setInquiries(inq.data);
    } catch {
      toast.error("Failed to load dashboard data");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(emptyPost); setDialogOpen(true); };
  const openEdit = (post) => { setEditing(post); setForm({ ...post }); setDialogOpen(true); };
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const savePost = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/blog/${editing.id}`, form);
        toast.success("Post updated");
      } else {
        await api.post("/admin/blog", form);
        toast.success("Post created");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const deletePost = async (id) => {
    try { await api.delete(`/admin/blog/${id}`); toast.success("Post deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const markRead = async (id) => {
    try { await api.patch(`/admin/inquiries/${id}/read`); load(); } catch (e) { console.error("mark read failed", e); }
  };

  const deleteInquiry = async (id) => {
    try { await api.delete(`/admin/inquiries/${id}`); toast.success("Inquiry deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const handleLogout = () => { logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <header className="border-b border-border bg-card sticky top-0 z-40">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO} alt={COMPANY.short} className="h-8 w-auto object-contain" />
            <span className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-muted-foreground hidden sm:inline">Content Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noreferrer" className="font-mono2 text-[11px] uppercase tracking-[0.15em] inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground" data-testid="view-site-link">
              View site <ExternalLink size={13} />
            </a>
            <span className="text-sm hidden md:inline text-muted-foreground">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn" className="gap-2">
              <LogOut size={16} /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-5 md:px-8 py-10">
        <h1 className="font-display text-4xl md:text-5xl mb-8">Dashboard</h1>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="mb-8" data-testid="admin-tabs">
            <TabsTrigger value="overview" data-testid="tab-overview" className="gap-2"><LayoutDashboard size={15} /> Overview</TabsTrigger>
            <TabsTrigger value="posts" data-testid="tab-posts" className="gap-2"><FileText size={15} /> Journal</TabsTrigger>
            <TabsTrigger value="inquiries" data-testid="tab-inquiries" className="gap-2">
              <Inbox size={15} /> Inquiries
              {stats?.unread_inquiries > 0 && <Badge className="ml-1 bg-primary text-primary-foreground">{stats.unread_inquiries}</Badge>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard label="Total Posts" value={stats?.total_posts ?? "—"} icon={FileText} />
              <StatCard label="Published" value={stats?.published_posts ?? "—"} icon={FileText} />
              <StatCard label="Inquiries" value={stats?.total_inquiries ?? "—"} icon={Inbox} />
              <StatCard label="Unread" value={stats?.unread_inquiries ?? "—"} icon={Mail} />
            </div>
          </TabsContent>

          <TabsContent value="posts">
            <div className="flex justify-between items-center mb-6">
              <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-muted-foreground">{posts.length} article(s)</p>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={openCreate} data-testid="new-post-btn" className="gap-2 rounded-none"><Plus size={16} /> New Post</Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-none" data-testid="post-dialog">
                  <DialogHeader>
                    <DialogTitle className="font-display text-2xl">{editing ? "Edit Post" : "New Post"}</DialogTitle>
                    <DialogDescription className="font-mono2 text-xs uppercase tracking-[0.15em] text-muted-foreground">
                      {editing ? "Update this journal article." : "Create a new journal article."}
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={savePost} className="space-y-4 mt-2">
                    <div>
                      <Label htmlFor="title">Title</Label>
                      <Input id="title" required value={form.title} onChange={update("title")} className="rounded-none mt-1.5" data-testid="post-title-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Category</Label>
                        <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                          <SelectTrigger className="rounded-none mt-1.5" data-testid="post-category-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="author">Author</Label>
                        <Input id="author" value={form.author} onChange={update("author")} className="rounded-none mt-1.5" data-testid="post-author-input" />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="cover">Cover image URL</Label>
                      <Input id="cover" value={form.cover_image} onChange={update("cover_image")} className="rounded-none mt-1.5" placeholder="https://…" data-testid="post-cover-input" />
                    </div>
                    <div>
                      <Label htmlFor="excerpt">Excerpt</Label>
                      <Textarea id="excerpt" required rows={2} value={form.excerpt} onChange={update("excerpt")} className="rounded-none mt-1.5 resize-none" data-testid="post-excerpt-input" />
                    </div>
                    <div>
                      <Label htmlFor="content">Content</Label>
                      <Textarea id="content" required rows={8} value={form.content} onChange={update("content")} className="rounded-none mt-1.5" placeholder="Separate paragraphs with a blank line" data-testid="post-content-input" />
                    </div>
                    <div className="flex items-center gap-3">
                      <Switch checked={form.published} onCheckedChange={(v) => setForm((f) => ({ ...f, published: v }))} data-testid="post-published-switch" />
                      <Label>Published</Label>
                    </div>
                    <DialogFooter>
                      <Button type="submit" disabled={saving} className="rounded-none" data-testid="save-post-btn">
                        {saving ? "Saving…" : editing ? "Update Post" : "Create Post"}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="border border-border divide-y divide-border bg-card">
              {posts.length === 0 && <p className="p-8 text-muted-foreground text-sm">No posts yet.</p>}
              {posts.map((p) => (
                <div key={p.id} className="flex items-center gap-4 p-4" data-testid={`post-row-${p.id}`}>
                  {p.cover_image && <img src={p.cover_image} alt="" className="w-16 h-12 object-cover shrink-0 hidden sm:block" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-primary">{p.category}</span>
                      {!p.published && <Badge variant="secondary" className="text-[10px] rounded-none">Draft</Badge>}
                    </div>
                    <h3 className="font-medium truncate">{p.title}</h3>
                    <p className="text-xs text-muted-foreground">{fmtDate(p.created_at)}</p>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" data-testid={`post-actions-${p.id}`}><MoreVertical size={18} /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="rounded-none">
                      <DropdownMenuItem onClick={() => openEdit(p)} data-testid={`edit-post-${p.id}`}><Pencil size={14} className="mr-2" /> Edit</DropdownMenuItem>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-primary" data-testid={`delete-post-${p.id}`}><Trash2 size={14} className="mr-2" /> Delete</DropdownMenuItem>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="rounded-none">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete this post?</AlertDialogTitle>
                            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deletePost(p.id)} className="rounded-none bg-primary" data-testid={`confirm-delete-post-${p.id}`}>Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="inquiries">
            <div className="border border-border divide-y divide-border bg-card">
              {inquiries.length === 0 && <p className="p-8 text-muted-foreground text-sm" data-testid="no-inquiries">No inquiries yet.</p>}
              {inquiries.map((inq) => (
                <div key={inq.id} className={`p-5 ${!inq.read ? "bg-muted/50" : ""}`} data-testid={`inquiry-row-${inq.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {!inq.read && <span className="w-2 h-2 bg-primary rounded-full" />}
                        <h3 className="font-medium">{inq.subject}</h3>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {inq.name} · <a href={`mailto:${inq.email}`} className="link-underline">{inq.email}</a>
                        {inq.company ? ` · ${inq.company}` : ""}{inq.phone ? ` · ${inq.phone}` : ""}
                      </p>
                      <p className="text-sm mt-3 text-foreground/80">{inq.message}</p>
                      <p className="text-xs text-muted-foreground mt-3 font-mono2">{fmtDate(inq.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {!inq.read && (
                        <Button variant="ghost" size="sm" onClick={() => markRead(inq.id)} data-testid={`mark-read-${inq.id}`}>Mark read</Button>
                      )}
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-primary" data-testid={`delete-inquiry-${inq.id}`}><Trash2 size={16} /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="rounded-none">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete this inquiry?</AlertDialogTitle>
                            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteInquiry(inq.id)} className="rounded-none bg-primary" data-testid={`confirm-delete-inquiry-${inq.id}`}>Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
